import React, { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ActivityFrame from '@/components/activity-frame/activity-frame';
import { useLocale } from "@/contexts/localeContext";
import { TableContextProvider } from '@/contexts/table/TableContextProvider';
import { TranslationKeys } from "@/contexts/TranslationKeys";
import { ListData } from '@/models/ListData';
import { ListQuery } from '@/models/ListQuery';
import { MultiSelectOption } from '@/models/MultiSelectOption';
import { appendFilter } from '@/utilities/filters';
import Label from '@/components/label/label';
import PaginatedSearchableSelectField from '@/components/selection-field/paginated-searchable-select-field';
import TableTab from "@/components/tabs/table-tab";
import { getDeviceEventActionTypeNamesWithPagination } from '@/services/deviceEventActionService';
import { deleteBioEventAction, getAllBioEventActions } from '@/services/bioEventActionService';
import BioEventActionColumns from '@/models/ColumnDefinitions/BioEventActionColumns';
import BioEventActionSearch from '@/models/search/BioEventActionSearch';
import BioEventActionModal from './biometric/BioEventActionModal';
import { DeleteEntityResult } from '@/models/services/DeleteEntityResult';

interface BioEventActionsProps {
    terminalId: string;
    bioAddressId: string;
}

const BioEventActions: FC<BioEventActionsProps> = ({ terminalId, bioAddressId }) => {
    const { translate } = useLocale();
    const [showModal, setShowModal] = useState(false);
    const [selectedEventOption, setSelectedEventOption] = useState('');
    const [selectedEventOptionLabel, setSelectedEventOptionLabel] = useState('');
    const [busy, setBusy] = useState(false);
    const [dataRefresh, setDataRefresh] = useState(() => (_dataMethod?: (query?: ListQuery) => Promise<ListData>) => { });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    };

    useEffect(() => {
        dataRefresh(getTableData);
    }, [selectedEventOption, terminalId]);

    const getTableData = (listParameters?: ListQuery) =>
        getAllBioEventActions({
            ...listParameters,
            filters: appendFilter(selectedEventOption, "selected event", 'eventType.id', 
                appendFilter(terminalId, "Selected terminal", 'terminal.id', listParameters?.filters ?? []))
        });

    const onEventTypeSelect = (_: any, item?: MultiSelectOption) => {
        setSelectedEventOption(item?.value ?? '');
        setSelectedEventOptionLabel(item?.label ?? '');
    };

    const handleOpenModal = async () => {
        if (!selectedEventOption) {
            toast.warn(translate.byKey(TranslationKeys.selectEventTypeFirst));
            return;
        }
        setShowModal(true);
    };

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteBioEventAction(id)
            .then(deleteResult => {
                    resolve(deleteResult);
                    setBusy(false);
                },
                deleteResult => {reject(deleteResult);
                    setBusy(false);
                })
                .finally(() => {
                    setBusy(false);
                });
        });
    }

    return <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white'>
        <TableContextProvider
            dataMethod={getTableData}
            tableDescriptor={BioEventActionColumns}
            searchDescriptor={BioEventActionSearch}
            useSearch
            useSort
            useTable
            usePagination
            deleteMethod={onDelete}
            manualDataRefresh={manualDataRefresh}>
            <TableTab
                title={translate.byKey(TranslationKeys.assignedActions)}
                selectRows={false}
                cyName='bioEventActions'>
                <div className="w-auto">
                    <div className="flex flex-row justify-between">
                        <div className="flex flex-col pr-6">
                            <Label htmlFor='eventOptions' className="text-xs text-gray-600 mt-1">
                                {translate.byKey(TranslationKeys.eventTypes)}:
                            </Label>
                        </div>
                        <div className="flex flex-col flex-1">
                            <PaginatedSearchableSelectField
                                name={translate.byKey(TranslationKeys.eventTypes)}
                                key="id"
                                accessor="id"
                                dataMethod={(listParameters?: ListQuery) => getDeviceEventActionTypeNamesWithPagination(false, {
                                    ...listParameters,
                                    filters: appendFilter(terminalId, 'Device Improx Id', 'deviceImprox.id', listParameters?.filters ?? [])
                                })}
                                searchAccessor={'eventType.nameOriginal'}
                                onSelect={onEventTypeSelect}
                                customSelectedOption={selectedEventOption}
                                selectedItemLabel={selectedEventOptionLabel}
                                onDelete={() => setSelectedEventOption('')} 
                                customerWidth='w-64'/>
                        </div>
                        <button type="button" className="flex ml-8 px-4 bg-lime rounded-lg shadow text-white text-sm items-center"
                            onClick={handleOpenModal}> {translate.byKey(TranslationKeys.add)}
                        </button>
                    </div>
                </div>
            </TableTab>
        </TableContextProvider>
        <BioEventActionModal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            setBusy={setBusy}
            terminalId={terminalId}
            bioAddressId={bioAddressId}
            eventTypeId={selectedEventOption}
            eventTypeLabel={selectedEventOptionLabel}/>
    </ActivityFrame>
};

export default BioEventActions;